import React, { useEffect, useMemo, useRef, useState } from "react";
import { InfoWindow, Polygon } from "@react-google-maps/api";
import { Empty, Select, Spin } from "antd";
import { Filter, MapPinned, RotateCcw } from "lucide-react";
import { useQuery } from "react-query";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import QuantitativeSurveyRightPanel from "./QuantitativeSurveyRightPanel";

const toComparable = (value = "") => value?.toString().trim().toUpperCase();

const getCenter = (path = []) => {
  if (!window.google || !path.length) return null;
  const bounds = new window.google.maps.LatLngBounds();
  path.forEach((point) => bounds.extend(point));
  return bounds.getCenter();
};

// const WARD_COLORS = [
//   "#1089ecff", "#92c409ff", "#f9bc2cff", "#f46a2fff", "#5c39ebff",
//   "#BDE7D7", "#F2CCE6", "#D7C2B0", "#F8D6D0", "#D0E7DE",
// ];
const WARD_COLORS = [
  "#1089ecff", "#1089ecff", "#1089ecff", "#1089ecff", "#1089ecff",
  "#1089ecff", "#1089ecff", "#1089ecff", "#1089ecff", "#1089ecff",
];

const QuantitativeSurveyMap = ({ polygonPaths = [], mapRef, clientId }) => {
  const axiosInstance = useAxiosInstance();
  const initialBoundsRef = useRef(null);

  const [selectedWard, setSelectedWard] = useState(null);
  const [hoveredWard, setHoveredWard] = useState(null);
  const [hoveredCenter, setHoveredCenter] = useState(null);
  const [infoWindowPos, setInfoWindowPos] = useState(null);

  // Capture initial bounds once polygons are ready
  useEffect(() => {
    if (!window.google || !polygonPaths.length || initialBoundsRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    polygonPaths.forEach((poly) => poly.path.forEach((p) => bounds.extend(p)));
    if (!bounds.isEmpty()) initialBoundsRef.current = bounds;
  }, [polygonPaths]);

  // Unique ward list derived from polygon paths
  const wardEntries = useMemo(() => {
    const seen = new Map();
    polygonPaths.forEach((poly, idx) => {
      if (!poly.name || seen.has(toComparable(poly.name))) return;
      seen.set(toComparable(poly.name), {
        name: poly.name,
        color: WARD_COLORS[idx % WARD_COLORS.length],
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [polygonPaths]);

  // Ward color map
  const wardColorMap = useMemo(() => {
    const map = {};
    wardEntries.forEach((w, i) => {
      map[toComparable(w.name)] = WARD_COLORS[i % WARD_COLORS.length];
    });
    return map;
  }, [wardEntries]);

  // Fetch ward analysis when a ward is selected
  const {
    data: analysis,
    isLoading: isAnalysisLoading,
  } = useQuery(
    ["qs-ward-analysis", clientId, selectedWard],
    async () => {
      const res = await axiosInstance.get("/api/quantitativeSurvey/wardAnalysis", {
        params: { clientId, ward: selectedWard || undefined },
      });
      return res?.data?.data || null;
    },
    {
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const zoomToPolygons = (items = [], padding = 80) => {
    if (!mapRef?.current || !window.google || !items.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    items.forEach((poly) => poly.path.forEach((p) => bounds.extend(p)));
    if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, padding);
  };

  const resetView = () => {
    setSelectedWard(null);
    setInfoWindowPos(null);
    if (mapRef?.current && initialBoundsRef.current) {
      mapRef.current.fitBounds(initialBoundsRef.current);
    }
  };

  const handleWardSelect = (value) => {
    if (!value) {
      resetView();
      return;
    }
    setSelectedWard(value);

    // Zoom to selected ward polygons
    const targets = polygonPaths.filter(
      (poly) => toComparable(poly.name) === toComparable(value)
    );
    zoomToPolygons(targets, 60);

    // Set info window position to center of first matching polygon
    if (targets.length) {
      const center = getCenter(targets[0].path);
      setInfoWindowPos(center);
    }
  };

  const handleWardClick = (poly) => {
    setSelectedWard(poly.name);
    const targets = polygonPaths.filter(
      (p) => toComparable(p.name) === toComparable(poly.name)
    );
    zoomToPolygons(targets, 60);
    const center = getCenter(targets[0]?.path || poly.path);
    setInfoWindowPos(center);
  };

  return (
    <>
      {/* --- Polygons --- */}
      {polygonPaths.map((poly, index) => {
        const key = toComparable(poly.name);
        const isSelected = selectedWard && toComparable(selectedWard) === key;
        const isHovered = hoveredWard && toComparable(hoveredWard) === key;
        const baseColor = wardColorMap[key] || "#C7E0F4";

        let fillColor = baseColor;
        let fillOpacity = isSelected ? 0.88 : isHovered ? 0.75 : 0.55;
        let strokeColor = isSelected ? "#0F3B63" : "#334155";
        let strokeWeight = isSelected ? 2.5 : isHovered ? 2 : 1.2;
        let strokeOpacity = isSelected ? 1 : isHovered ? 0.9 : 0.5;

        if (selectedWard && !isSelected) {
          fillOpacity = 0.12;
          strokeOpacity = 0.18;
        }

        return (
          <Polygon
            key={`qs-ward-${poly.name}-${index}`}
            paths={poly.path}
            options={{
              fillColor,
              fillOpacity,
              strokeColor,
              strokeWeight,
              strokeOpacity,
              clickable: true,
              zIndex: isSelected ? 900 : isHovered ? 500 : 100,
            }}
            onMouseOver={() => {
              setHoveredWard(poly.name);
              setHoveredCenter(getCenter(poly.path));
            }}
            onMouseOut={() => {
              setHoveredWard(null);
              setHoveredCenter(null);
            }}
            onClick={() => handleWardClick(poly)}
          />
        );
      })}

      {/* --- Hover tooltip --- */}
      {hoveredWard && hoveredCenter && !selectedWard && (
        <InfoWindow position={hoveredCenter}>
          <div className="min-w-[160px] rounded-2xl bg-white/95 p-3 text-black shadow-xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Ward</p>
            <h3 className="mt-1 text-base font-semibold text-[#123865]">{hoveredWard}</h3>
            <p className="mt-1 text-xs font-medium text-[#16608D]">Click to view SWOT analysis</p>
          </div>
        </InfoWindow>
      )}

      {/* --- Selected ward info window popup --- */}
      {selectedWard && infoWindowPos && (
        <InfoWindow
          position={infoWindowPos}
          onCloseClick={resetView}
        >
          <div className="min-w-[200px] overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-xl">
            <div className="bg-[linear-gradient(135deg,#0F3B63_0%,#16608D_100%)] px-4 py-3 text-white">
              <p className="text-xs uppercase tracking-[0.22em] !text-white/70">Selected Ward</p>
              <h3 className="mt-1 text-base font-semibold">{selectedWard}</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 px-4 py-3 text-black">
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] text-slate-500">Population</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-800">
                  {analysis?.totalPopulation?? "—"}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] text-slate-500">Polling Stations</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-800">
                  {analysis?.totalPollingStations ?? "—"}
                </p>
              </div>
              <div className="col-span-2 rounded-xl bg-slate-50 px-3 py-2">
                <p className="text-[11px] text-slate-500">Municipal Corporator</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-800">
                  {analysis?.municipalCorporator || "—"}
                </p>
              </div>
              <div className="col-span-2 rounded-xl bg-amber-50 px-3 py-2">
                <p className="text-[11px] text-amber-600">Issues</p>
                <p className="mt-0.5 text-xs text-slate-700">{analysis?.issues || "—"}</p>
              </div>
            </div>
            <div className="border-t border-slate-100 px-4 py-2 text-center text-xs text-slate-500">
              See right panel for SWOT analysis
            </div>
          </div>
        </InfoWindow>
      )}

      {/* --- Left filter panel --- */}
      <div className="absolute left-4 top-4 z-[2300] w-[300px] max-w-[calc(100vw-2rem)] rounded-[24px] border border-white/30 bg-white/78 p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl">
        <div className="mb-3 flex items-center gap-2 text-[#123865]">
          <Filter size={18} />
          <span className="text-sm font-semibold">Quantitative Survey Filters</span>
        </div>

        <div className="space-y-3">
          <Select
            placeholder="Select Ward"
            value={selectedWard || undefined}
            onChange={handleWardSelect}
            options={wardEntries.map((w) => ({ label: w.name, value: w.name }))}
            allowClear
            showSearch
            getPopupContainer={(trigger) => trigger.parentNode}
            style={{ width: "100%" }}
            notFoundContent={
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No wards found" />
            }
          />

          <div className="flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <MapPinned size={15} className="text-[#16608D]" />
            <span>{wardEntries.length} wards mapped</span>
          </div>

          <button
            type="button"
            onClick={resetView}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#123865] px-4 py-2 text-sm font-medium !text-white transition hover:bg-[#0F3B63]"
          >
            <RotateCcw size={16} />
            Reset Map
          </button>
        </div>
      </div>

      {/* --- Bottom legend --- */}
      {/* <div className="absolute bottom-5 left-4 z-[2200] max-h-[38vh] w-[270px] overflow-y-auto rounded-[24px] border border-white/30 bg-white/76 p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl">
        <p className="mb-3 text-xs uppercase tracking-[0.2em] text-slate-500">Wards Legend</p>
        <div className="space-y-1.5">
          {wardEntries.map((w, i) => (
            <button
              key={w.name}
              type="button"
              onClick={() => handleWardSelect(w.name)}
              className="flex w-full items-center gap-3 rounded-2xl border border-slate-100 px-3 py-2 text-left transition hover:border-[#78C5E7] hover:bg-[#EFF8FD]"
            >
              <span
                className="h-3.5 w-3.5 rounded-md border border-slate-200 flex-shrink-0"
                style={{ backgroundColor: WARD_COLORS[i % WARD_COLORS.length] }}
              />
              <span className="truncate text-sm font-medium text-slate-700">{w.name}</span>
            </button>
          ))}
        </div>
      </div> */}

      {/* --- Right panel --- */}
      {isAnalysisLoading ? (
        <div className="absolute right-4 top-[10%] z-[2200] flex h-[200px] w-[500px] items-center justify-center rounded-[28px] border border-white/30 bg-white/70 shadow-[0_24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl">
          <Spin size="large" />
        </div>
      ) : (
        <QuantitativeSurveyRightPanel
          analysis={analysis}
          selectedWard={selectedWard}
          isLoading={isAnalysisLoading}
        />
      )}
    </>
  );
};

export default QuantitativeSurveyMap;
